import styled, { type DefaultTheme } from 'styled-components';

import { Chip } from '../../../atoms/chip/Chip';
import { InteractionLayer } from '../../../atoms/interaction-layer/InteractionLayer';

type RadioButtonOptionType = { id: boolean | string; label: string };

type RadioButtonSize = keyof DefaultTheme['sizing'];

export interface RadioButtonInputProps {
	checked?: boolean;
	disabled?: boolean;
	isChip?: boolean;
	item?: RadioButtonOptionType;
	onPress: () => void;
	pressableLabels?: boolean;
	size?: RadioButtonSize;
	testID?: string;
}

export const RadioButtonInput = ({
	checked,
	disabled,
	isChip = false,
	item,
	onPress,
	pressableLabels,
	size = 'medium',
	testID = 'radio-button-input'
}: RadioButtonInputProps) => (
	<S.Wrapper $size={size}>
		{isChip && item?.label && (
			<S.RadioButtonChip
				isSelected={checked}
				testID={testID + '-chip'}
				text={item?.label}
				value={item?.id.toString()}
				onClick={!disabled ? onPress : undefined}
			/>
		)}
		{!isChip && (
			<S.RadioButtonCircleInteractionLayer disabled={disabled} testID={testID + '-radio'} onClick={onPress}>
				<S.RadioButtonOutterCircle>
					<S.RadioButtonInnerCircle checked={checked} />
				</S.RadioButtonOutterCircle>
			</S.RadioButtonCircleInteractionLayer>
		)}
		{!isChip && item?.label && (
			<S.RadioButtonLabel
				data-testid={testID + '-radio-label'}
				id={item?.id.toString()}
				onClick={pressableLabels && !disabled ? onPress : undefined}
			>
				{item?.label}
			</S.RadioButtonLabel>
		)}
	</S.Wrapper>
);

namespace S {
	export const Wrapper = styled.div<{ $size: RadioButtonSize }>`
		display: flex;
		--radio-button-size: ${({ $size, theme }) => `${theme.sizing[$size]}`};
	`;
	export const RadioButtonCircleInteractionLayer = styled(InteractionLayer)`
		width: calc(var(--radio-button-size) + 2px);
		height: calc(var(--radio-button-size) + 2px);
		border: ${({ theme }) => `${theme.border.width.medium}`};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const RadioButtonOutterCircle = styled.div`
		box-sizing: content-box;
		width: var(--radio-button-size);
		height: var(--radio-button-size);
		border: ${({ theme }) => `${theme.border.width.medium} solid ${theme.colors.borders.brand.enabled}`};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const RadioButtonInnerCircle = styled.div<{ checked?: boolean }>`
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: calc(var(--radio-button-size) * 0.625);
		height: calc(var(--radio-button-size) * 0.625);
		background: ${({ checked, theme }) => checked && `${theme.colors.borders.brand.enabled}`};
		${({ theme }) => theme.border.radius.circle};
	`;

	export const RadioButtonLabel = styled.label`
		box-sizing: content-box;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: left;
		margin: 0 ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const RadioButtonChip = styled(Chip)<{ isSelected?: boolean }>`
		pointer-events: ${({ isSelected }) => (isSelected ? 'none' : 'auto')};
	`;
}
